/* eslint-disable max-lines */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-func/max-lines-per-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isNil, toLower } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { message, UploadFile } from '@fuxi/eevee-ui';
import {
  Task,
  FileData,
  CheckItem,
  StageStatus,
  TaskMessage,
  AddVersionType,
  TaskFileDetail,
  QIResultStatus,
  TaskQueueStatus,
  TaskQueueDetail,
} from 'common/types';
import dayjs from 'dayjs';
import service from '@/service';
import { NameRule } from '@/pages/AddVersion/help';
import {
  CurrentPage,
  MAX_BYTES_VERSION_FILES,
  SubmitStatus,
  QIStep,
  mapStatusToCurrentStep,
  mapStatusToCurrentPage,
} from '@/constants';
import { RootState } from './rootReducer';
import { AppThunk } from '.';
import { setCurTaskId, setIsAddVersionShow } from './task';
import { getTaskQueue, setIsTaskQueueShow, updateTaskQueue } from './taskQueue';

// 格式化版本质检规则
export const formatVersionRule = (rule: CheckItem[]) => {
  let versionFileType: string[] = [];
  let allFileNameRules: NameRule[] = [];
  let requiredFileNameRules: NameRule[] = [];
  rule?.forEach(checkItem => {
    if (checkItem?.fileType) {
      const reducesType = checkItem.fileType?.filter((type: string) => toLower(type));
      versionFileType = versionFileType.concat(reducesType);
    }
    try {
      const isFile = !!checkItem?.fileName;
      if (isFile) {
        // 过滤正则中的变量模版校验规则
        const varRegex = new RegExp('\\$\\{.*\\}');
        // 1.修正json里的fileName正则的变量
        const fileNameReg = checkItem?.fileName?.replace(varRegex, '.*');
        // 2.补充文件格式正则校验
        const fileTypes = checkItem?.fileType?.map(type => toLower(type));
        const reg = fileNameReg;
        allFileNameRules.push({
          reg,
          desc: (checkItem?.fileName_desc || checkItem?.fileNameDesc) ?? '',
          formats: fileTypes as string[],
        });
        checkItem?.missing === 'Error' &&
          requiredFileNameRules.push({
            reg,
            desc: (checkItem?.fileName_desc || checkItem?.fileNameDesc) ?? '',
            formats: fileTypes as string[],
          });
      }
    } catch (e) {
      console.error('解析任务规则出错:', e);
    }
  });
  return {
    versionFileType: (Array.from(new Set(versionFileType)) as string[]) || [],
    requiredFileNameRules: Array.from(new Set(requiredFileNameRules) || []),
    allFileNameRules,
    taskTemplate: rule,
  };
};

// 获取子任务的质检规则
const getVersionFileType = async curTaskId => {
  const { rule, versionFileSizeLimitedBytes } = (await service.exportVersions.getVersionRule(curTaskId)) || {};
  const formatted = formatVersionRule(rule);
  return {
    ...formatted,
    versionFileSizeLimitedBytes: versionFileSizeLimitedBytes || MAX_BYTES_VERSION_FILES,
  };
};
// 获取任务模板的质检规则，用于上传资产
const getCheckItemFromTemplate = async (templateId: number) => {
  const { data } = await service.taskTempSetting.getParentTemplateInfo(templateId, 37);
  const { templateJson } = data;
  if (templateJson.length > 0) {
    let rule = templateJson[0].template;
    const formatted = formatVersionRule(rule);
    return {
      ...formatted,
      versionFileSizeLimitedBytes: MAX_BYTES_VERSION_FILES,
    };
  } else {
    return {
      versionFileType: [],
      versionFileSizeLimitedBytes: MAX_BYTES_VERSION_FILES,
      requiredFileNameRules: [],
      allFileNameRules: [],
    };
  }
};

interface VersionInfo {
  // uuid?: string; // 标识上传文件，以dcc与服务端通信。也是消息队列的主键， 后端返回VersionInfo即可还原
  versionId?: number; // 提交成功后，服务端生成
  description?: string;
  versionFiles?: Array<TaskFileDetail>;
  attachmentFiles?: Array<TaskFileDetail>;
  replacedVersionFiles?: Array<any>;
}

interface AddVersionStatusState {
  currentStep?: QIStep; // 当前步骤，由于AddVersion页面包含了提交中和质检中的状态，不离开页面，所以需要区分
  taskStatus?: TaskQueueStatus; //任务质检状态
  submitStatus?: SubmitStatus; // 版本提交状态
  currentPage?: CurrentPage; // 当前页面，用于区分上传页面，预览页面，与提交版本页面。
  taskProgress?: string; // 上传 或  质检 进度
  queuePos?: number; // 质检等待队列排队位置
}

export type RuleInfo = {
  versionFileType?: Array<string>;
  versionFileSizeLimitedBytes: number;
  requiredFileNameRules?: NameRule[];
  allFileNameRules?: NameRule[];
};

export interface ArtAssetInfo {
  assetName: string;
  assetType: string;
  price: number;
  sign?: boolean; // 是否签署协议
  tags?: string;
}

export interface AddVersionState {
  curUuid: string; // 当前版本uuid
  taskInfo?: Task; // 任务的信息
  // 前端校验文案/规则
  ruleInfo: RuleInfo;
  versionInfo: VersionInfo;
  status: AddVersionStatusState;
  type: AddVersionType; // 上传版本文件还是资产
  assetInfo?: ArtAssetInfo;
}

const initialState: AddVersionState = {
  curUuid: '',
  ruleInfo: {
    versionFileSizeLimitedBytes: MAX_BYTES_VERSION_FILES,
  },
  status: { currentPage: CurrentPage.Upload, currentStep: QIStep.Prepare },
  versionInfo: {},
  type: AddVersionType.Version,
};

const addVersion = createSlice({
  name: 'add-version',
  initialState,
  reducers: {
    setCurUuid(state, { payload }: PayloadAction<string>) {
      state.curUuid = payload;
    },
    updateValue(state, { payload }: PayloadAction<Partial<AddVersionState>>) {
      const newState = Object.assign({}, state, payload);
      return newState;
    },
    updateVersionInfo(state, { payload }: PayloadAction<Partial<VersionInfo>>) {
      state.versionInfo = { ...state.versionInfo, ...payload };
    },
    clearStoreCache(state) {
      const newState = Object.assign({}, state, initialState);
      return newState;
    },
    updateStatus(state, { payload }: PayloadAction<AddVersionStatusState>) {
      state.status = { ...state.status, ...payload };
    },
    updateArtAssetInfo(state, { payload }: PayloadAction<ArtAssetInfo | undefined>) {
      state.assetInfo = payload;
    },
  },
});

export const { setCurUuid, updateValue, updateVersionInfo, clearStoreCache, updateStatus, updateArtAssetInfo } =
  addVersion.actions;
export const selectAddVersion = (state: RootState) => state.addVersion;
export const selectCurUuid = (state: RootState) => state.addVersion.curUuid;
export const selectAddVersionStatus = (state: RootState) => state.addVersion.status;
export const selectVersion = (state: RootState) => state.addVersion.versionInfo;
export const selectArtAssetInfo = (state: RootState) => state.addVersion.assetInfo;
// export const selectAttachmentFiles = (state: RootState) => state.addVersion.attachmentFiles;

/**
 * initialTaskRule 获取任务信息和规则
 * @param { type, id}  上传类型， 任务id / 模板id
 */
const initialTaskRule =
  (params: { id: number; type: AddVersionType; taskName: string; isNew: boolean }): AppThunk =>
  async (dispatch, getState) => {
    const { id, type, taskName } = params;
    if (type === AddVersionType.Version) {
      let taskInfo = await service.tasks.getTaskInfo({ taskId: id });
      taskInfo = {
        ...taskInfo,
        projectConfig: JSON.stringify({
          scale: 1,
          refModels: [],
        }),
      };
      if (taskInfo?.projectConfig) {
        try {
          Object.assign(taskInfo, { projectConfig: JSON.parse(taskInfo.projectConfig) });
        } catch {
          console.error('无法解析task.projectConfig');
        }
      }
      const ruleInfo = await getVersionFileType(id);
      return {
        taskInfo,
        ruleInfo,

        status: { currentStep: QIStep.Prepare, currentPage: CurrentPage.Upload },
        type: type, // 上传版本文件还是资产
      };
    } else {
      // TODO: 资产上传的模板规则获取
      // mock一个资产上传任务
      const taskInfo = {
        uniqueId: id,
        taskSubject: taskName,
        stage: '',
        taskStatus: {
          result: QIResultStatus.WAITING_UPLOADING,
        },
        status: StageStatus.PendingProcessing,
        projectConfig: '{}',
        externalContacts: [],
      };

      const ruleInfo = await getCheckItemFromTemplate(id);
      // dispatch(updateValue({ taskInfo, ruleInfo }));
      return {
        ruleInfo,
        taskInfo,
      };
    }
  };

/**
 * 开始上传的初始化：
 * 1.清除版本缓存
 * 2.获取任务规则和状态 @func initialTaskRule
 * 3.生成新版本的uuid
 * @param taskId 对应任务
 */
export const startAddVersion =
  (params: { type: AddVersionType; id: number }): AppThunk =>
  async (dispatch, getState) => {
    const { type, id } = params;
    const store = getState();
    const state = store?.addVersion;
    const taskQueue = store?.taskQueue?.taskQueue || [];
    const uploadingTaskQueue = store?.uploadingTaskQueue?.queue || [];
    const pendingTasks = [...taskQueue, ...uploadingTaskQueue]?.filter(
      task => task.taskStatus.result !== QIResultStatus.UPLOAD_SUCCESS,
    );
    const hasSomeTaskPending = pendingTasks?.some(({ taskId: pendingTaskId }) => Number(id) === Number(pendingTaskId));

    if (hasSomeTaskPending && type === AddVersionType.Version) {
      message.error('该任务正在进行中，请查看任务队列');
      return;
    }

    console.log('startAddVersion', { state, id });
    // if (id.toString() !== getState()?.addVersion?.taskInfo?.uniqueId?.toString()) {
    dispatch(setCurTaskId(id));
    dispatch(setIsTaskQueueShow(false));
    dispatch(clearStoreCache());
    dispatch(updateVersionInfo({}));
    //如果taskId有变更，更新规则
    // }
    const res = await dispatch(
      initialTaskRule({
        id,
        type,
        taskName: type === AddVersionType.ArtAsset ? `资产上传-${dayjs().format('YYYY-MM-DD HH:mm:ss')}` : '',
        isNew: true,
      }),
    );
    const { data } = await service.addVersion.generateUUid(id);
    if (res && data) {
      console.log('设置新的uuid拉', data.uuid, res);

      dispatch(
        updateValue({
          ...res,
          curUuid: data.uuid,
          versionInfo: {
            versionFiles: [],
            description: '',
          },
        }),
      );
    }

    dispatch(setIsAddVersionShow(true));
  };

//
/**
 * 提交版本：
 * 1.清除版本缓存
 * 2.获取任务规则和状态 @func initialTaskRule
 * 3.生成新版本的uuid
 * @param taskId 对应任务
 */
export const submitVersion = (): AppThunk => async (dispatch, getState) => {
  dispatch(
    updateStatus({
      taskStatus: {
        result: QIResultStatus.UPLOADING,
      },
      submitStatus: SubmitStatus.PROCESSING,
      currentStep: QIStep.Submitting,
      currentPage: CurrentPage.Submit,
    }),
  );
  const state = getState()?.addVersion;
  const { versionInfo, taskInfo, type, assetInfo, curUuid } = state;

  if (type === AddVersionType.ArtAsset) {
    if (!assetInfo) {
      message.error('请先补充资产信息');
      return;
    }
    try {
      const data = await service.taskVersionServer.addArtAssetToSS({
        uuid: curUuid ?? '',
        assetName: assetInfo?.assetName,
        price: assetInfo?.price,
        assetType: assetInfo?.assetType,
      });

      const isSuc = data?.code === 0;
      // fos服务状态码
      const isRedisExpired = data?.code === 499;
      if (isRedisExpired) {
        message.error('该资产已过期，请重新触发质检', 5);
      }
      if (!isSuc) {
        dispatch(
          updateStatus({
            submitStatus: SubmitStatus.ERROR,
            currentStep: QIStep.Submitting,
            taskStatus: { result: QIResultStatus.UPLOAD_ERROR },
          }),
        );
        throw new Error();
      } else {
        dispatch(updateStatus({ submitStatus: SubmitStatus.PROCESSING, currentStep: QIStep.Submitting }));
        dispatch(updateVersionInfo({ versionId: data?.data?.version_id }));
      }
    } catch (e) {
      console.error(e);
      dispatch(
        updateStatus({
          submitStatus: SubmitStatus.ERROR,
          currentStep: QIStep.SubmitError,
          taskStatus: { result: QIResultStatus.UPLOAD_ERROR },
        }),
      );
      return;
    }
  } else {
    try {
      const data = await service.addVersion.addVersionToServer({
        taskId: taskInfo?.uniqueId!,
        uuid: curUuid!,
        description: versionInfo?.description,
      });
      const isSuc = data?.code === 0;
      // fos服务状态码，需要改成artbackend的服务状态码
      const isRedisExpired = data?.code === 499 || data?.code === 199007;
      if (isRedisExpired) {
        message.error('该任务已过期，请重新触发质检', 5);
      }

      if (!isSuc) {
        dispatch(
          updateStatus({
            submitStatus: SubmitStatus.ERROR,
            currentStep: QIStep.Submitting,
            taskStatus: { result: QIResultStatus.UPLOAD_ERROR },
          }),
        );
        throw new Error();
      } else {
        dispatch(updateStatus({ submitStatus: SubmitStatus.PROCESSING, currentStep: QIStep.Submitting }));
        dispatch(updateVersionInfo({ versionId: data?.data?.version_id }));
      }
    } catch (e) {
      console.error(e);
      dispatch(updateStatus({ submitStatus: SubmitStatus.ERROR, currentStep: QIStep.SubmitError }));
    }
  }
};
/**
 * parseTaskMsgToStatus  转换后端消息至前端状态
 * @param uuid 对应版本任务
 */
const parseTaskMsgToStatus = (taskMsg: TaskQueueDetail, uuid: string) => {
  const update = {};
  // 映射taskStatus的result字段到currentStep
  const { result } = taskMsg?.taskStatus;
  const currentStep = mapStatusToCurrentStep(result as QIResultStatus);
  const currentPage = mapStatusToCurrentPage(result as QIResultStatus);
  Object.assign(update, {
    curUuid: uuid,
    versionInfo: {
      versionFiles: taskMsg?.fileList,
      description: taskMsg?.description,
    },
    status: {
      currentStep: currentStep,
      currentPage: currentPage,
      taskStatus: taskMsg?.taskStatus,
      taskProgress: taskMsg.taskProgress,
    },
    type: taskMsg.type,
  });
  return update;
};
/**
 * fetchVersionInfo 从后端获取提交任务: 已上传的版本任务，获取服务端缓存恢复
 * @param uuid 对应版本任务
 */
export const fetchVersionInfo =
  (uuid?: string): AppThunk =>
  async (dispatch, getState) => {
    const isAddVersionShow = getState()?.task.isAddVersionShow;
    const currentStep = getState()?.addVersion?.status?.currentStep;
    const uploadingTaskQueue = getState()?.uploadingTaskQueue?.queue || [];

    const currentUuid = uuid;
    if (!currentUuid) {
      message.error('uuid不存在');
      console.error('uuid不存在');
      return;
    }
    let update = {};
    const isInUploadingQueue = uploadingTaskQueue.find(({ uuid }) => uuid === currentUuid);
    if (isInUploadingQueue) {
      const task = isInUploadingQueue;
      Object.assign(update, parseTaskMsgToStatus(task, currentUuid));
      dispatch(updateValue(update));
      dispatch(setIsAddVersionShow(true));
      return;
    }

    const { data: taskQueueList, code } = await service.taskQueue.getListBackground({ versionId: currentUuid });
    // fos服务状态码
    if (code === 499) {
      // 任务状态重置，更新uuid
      const taskStatus = {
        result: QIResultStatus.WAITING_UPLOADING,
        detail: null,
        message: [],
      };
      const currentStep = mapStatusToCurrentStep(taskStatus.result);
      const currentPage = mapStatusToCurrentPage(taskStatus.result);
      const taskId = getState()?.addVersion?.taskInfo?.uniqueId;

      Object.assign(update, {
        curUuid: currentUuid,
        versionInfo: {
          versionFiles: [],
          description: '',
          attachmentFiles: [],
        },
        status: {
          currentStep: currentStep,
          currentPage: currentPage,
          taskStatus: taskStatus,
          taskProgress: 0,
        },
      });
      dispatch(updateValue(update));
      //刷新任务队列
      if (uuid) {
        dispatch(getTaskQueue());
      }
      return;
    }

    // 在taskQueueList中寻找相同uuid的任务
    const task = taskQueueList.find(({ uuid }) => uuid === currentUuid);
    if (!task) {
      return;
    }
    Object.assign(update, parseTaskMsgToStatus(task, currentUuid));
    dispatch(updateValue(update));
    dispatch(updateTaskQueue(update, update?.versionInfo?.versionFiles));
  };
/**
 * reStoreMessageContext 恢复消息队列的提交状态
 * 分两种情况
 * 1.上传中的版本任务，在context内部获取上传缓存恢复
 * 2.已上传的版本任务，获取服务端缓存恢复
 * @param task 对应版本任务
 */
export const reStoreMessageContext =
  (task: TaskQueueDetail): AppThunk =>
  async (dispatch, getState) => {
    const { taskId, uuid, taskStatus, taskProgress, fileList, fileStatus, type, taskName, description } = task;
    const state = getState()?.addVersion;
    console.log('ReStoreMessageContext!!', { state, task });
    dispatch(setIsTaskQueueShow(false));
    dispatch(setCurTaskId(taskId));

    // 分情况，文件上传的消息不走后端缓存
    const currentStep = mapStatusToCurrentStep(taskStatus.result);

    const isUploading = currentStep === QIStep.Uploading;
    let update = {};
    if (isUploading) {
      // 从context中获取文件信息
      const { result } = taskStatus;
      const currentStep = mapStatusToCurrentStep(result);
      const currentPage = mapStatusToCurrentPage(result);

      // if (taskId.toString() !== taskInfo?.uniqueId.toString()) {
      // 获取任务规则
      const data = await dispatch(initialTaskRule({ id: taskId, type, taskName: taskName ?? '', isNew: false }));
      Object.assign(update, {
        curUuid: uuid,
        versionInfo: { versionFiles: fileList },
        ruleInfo: data?.ruleInfo,
        status: {
          currentStep: currentStep,
          currentPage: currentPage,
          taskStatus: taskStatus,
          taskProgress: taskProgress,
        },
        type,
      });
      dispatch(updateValue(update));
      dispatch(setIsAddVersionShow(true));
      // }
    } else {
      // 获取任务规则
      const data = await dispatch(initialTaskRule({ id: taskId, type, taskName: taskName ?? '', isNew: false }));
      dispatch(updateValue({ ...data, curUuid: uuid }));

      // 根据uuid获取版本信息
      dispatch(fetchVersionInfo(uuid));
      dispatch(setIsAddVersionShow(true));
    }
  };
/**
 * switchToBackend 切换至后台
 * 分两种情况
 * 1.上传中的版本任务，在context内部获取上传缓存恢复
 * 2.已上传的版本任务，获取服务端缓存恢复
 * @param task 对应版本任务
 */
export const switchToBackend = (): AppThunk => async (dispatch, getState) => {
  dispatch(setIsAddVersionShow(false));
  dispatch(clearStoreCache());

  // dispatch(getTaskQueue());
  dispatch(setIsTaskQueueShow(true));
};

/**
 *  updateCurrentTask 更新当前版本任务进度
 * 1.任务进度
 * 2.上传进度
 * @param taskMessage
 */
export const updateCurrentTask =
  (taskMessage: TaskMessage): AppThunk =>
  async (dispatch, getState) => {
    const state = getState()?.addVersion;
    const versionInfo = state?.versionInfo || {};
    const curUuid = state?.curUuid;
    const uploadingTaskQueue = getState()?.uploadingTaskQueue?.queue || [];
    const taskQueue = getState()?.taskQueue?.taskQueue || [];
    const queues = [...uploadingTaskQueue, ...taskQueue];
    // 在queue中寻找相同uuid的任务
    const task = queues.find(({ uuid }) => uuid === taskMessage.uuid);

    if (!task) {
      return;
    }

    const { fileList: versionFiles, taskStatus, description } = task;

    const {
      fileId,
      taskProgress,
      fileProgress,
      fileStatus,
      fosFileId,
      isReplaced,
      uuid,
      taskStatus: taskStatusMessage,
    } = taskMessage;
    let taskUpdate = {};
    if (taskMessage.uuid === task.uuid) {
      // 修改消息对应uuid任务队列状态
      const result = taskStatusMessage?.result;
      if (result && taskProgress) {
        const currentStep = mapStatusToCurrentStep(result);

        Object.assign(taskUpdate, { taskStatus: { ...task.taskStatus, ...taskStatusMessage }, currentStep });
      }
      taskProgress && Object.assign(taskUpdate, { taskProgress });
    }

    //如果当前的uuid与消息队列的uuid相同，更新curTask状态和currentPage
    if (curUuid === taskMessage.uuid) {
      // 在versionFiles找到fileId与fileId相同的文件，修改其状态
      const newVersionFiles = isNil(fileId)
        ? versionFiles
        : versionFiles?.map(file => {
            if (fileId === file.fileId) {
              const data = parseFileToStore({ ...file, fileStatus, fileProgress, fosFileId });
              if (isReplaced) {
                data.isReplaced = true;
              }
              return data;
            } else {
              return parseFileToStore(file);
            }
          }) || [];
      if (taskStatusMessage?.result && taskProgress) {
        const currentPage = mapStatusToCurrentPage(taskStatusMessage.result);
        Object.assign(taskUpdate, { currentPage });
      }

      dispatch(
        updateVersionInfo({
          ...versionInfo,
          versionFiles: newVersionFiles?.length > 0 ? newVersionFiles : versionInfo.versionFiles, //在触发质检完成时，会获取一次新的queue，这时候没有返回fileList，所以需要保留之前的fileList
        }),
      );
      dispatch(
        updateStatus({
          ...taskStatus,
          ...taskUpdate,
        }),
      );
    }
  };
export default addVersion.reducer;

/**
 * parseFileToStore 解析file对象为store可存储结构
 * @param fileData file对象
 * @return store可存储文件结构
 */
export const parseFileToStore = (fileData: FileData): TaskFileDetail => {
  return {
    ...fileData,
    key: (fileData as TaskFileDetail).fileId || (fileData as UploadFile).uid,
    format: (fileData as TaskFileDetail).format,
    fileId: (fileData as TaskFileDetail).fileId || (fileData as UploadFile).uid,
    name: fileData.name || (fileData as UploadFile).name,
    fileName: fileData.fileName || (fileData as UploadFile).name,
    path: (fileData as TaskFileDetail).path ?? '',
    fileStatus: (fileData as TaskFileDetail).fileStatus,
    fileProgress: (fileData as TaskFileDetail).fileProgress,
    info: (fileData as TaskFileDetail).info,
    fosFileId: (fileData as TaskFileDetail).fosFileId,
    isReplaced: (fileData as TaskFileDetail).isReplaced,
  };
};
